import { useCallback, useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import {
  CAD_PARCEL_STOCK_STYLE,
  EMPTY_SELECTED_LOT_FILTER,
  buildSelectedLotIdFilter,
} from '../../Map/styles/mapTheme';
import { whenMapStyleReady } from '../../Map/hooks/useListPinsLayer';
import {
  computeFeatureCollectionBbox,
  computeGeometryBbox,
  normalizeCadFeatureCollection,
} from '../utils/cadGeometry';
import {
  buildCadParcelTooltipHtml,
  buildCadShareParcelTooltipHtml,
  decorateCadParcelsForMap,
  getCadParcelId,
} from '../utils/cadParcelDisplay';
import {
  buildCadLotChipImageExpression,
  cadLotChipIconOpacityExpression,
  cadLotChipIconSizeExpression,
  ensureCadLotChipImages,
} from '../utils/cadLotChip';
import { queryReferencePlaceAtPoint } from '../../Map/utils/referencePlaces';
import { queryCadProjectPinAtPoint, moveCadProjectPinsForeground } from '../utils/cadProjectPinLayers';
import {
  CAD_LOT_KIND_FILTER,
  isCadSellableLot,
} from '../utils/cadMasterplan';
import {
  bumpCadSymbolOpEpoch,
  scheduleCadSymbolOp,
} from '../utils/afterCadSymbolPlacement';
import {
  CAD_MASTERPLAN_LAYER_IDS,
  CAD_OVERLAY_HIT_LAYER_IDS,
  CAD_TREE_LAYER_IDS,
  CAD_GEOJSON_SOURCE_OPTIONS,
  ensureCadMasterplanLayers,
  keepCadTreesAboveLots,
  removeCadMasterplanLayers,
} from '../utils/cadMasterplanLayers';
import {
  buildCadFillColorExpression,
  buildCadFillOpacityExpression,
  buildCadShareFillOpacityExpression,
  buildCadLineColorExpression,
  buildCadLineOpacityExpression,
  buildCadShareLineOpacityExpression,
  visibleCadStockStatusIds,
} from '../utils/cadStockStatus';

export const CAD_SOURCE_ID = 'cad-parcels';
export const CAD_FILL_LAYER_ID = 'cad-parcels-fill';
export const CAD_LINE_LAYER_ID = 'cad-parcels-line';
export const CAD_HOVER_LAYER_ID = 'cad-parcels-hover';
export const CAD_SELECTED_GLOW_LAYER_ID = 'cad-parcels-selected-glow';
export const CAD_SELECTED_LAYER_ID = 'cad-parcels-selected';
export const CAD_LABEL_LAYER_ID = 'cad-parcels-label';

export {
  CAD_GREEN_LAYER_ID,
  CAD_OVERLAY_HIT_LAYER_IDS,
  CAD_SIDEWALK_LAYER_ID,
  CAD_SITE_LAYER_ID,
  CAD_STREET_LAYER_ID,
  CAD_TREES_LAYER_ID,
  cadOverlayHitLayerIds,
  queryCadOverlayAtPoint,
  resolveCadOverlayInsertBeforeId,
} from '../utils/cadMasterplanLayers';

const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };
const CAD_PICK_LAYER_IDS = [CAD_FILL_LAYER_ID, CAD_LABEL_LAYER_ID];

function cadPickLayerIds(map, includeContext = false) {
  const ids = includeContext
    ? [...CAD_PICK_LAYER_IDS, ...CAD_OVERLAY_HIT_LAYER_IDS]
    : CAD_PICK_LAYER_IDS;
  return ids.filter((id) => map.getLayer(id));
}

function queryCadParcelAtPoint(map, point, includeContext = false) {
  if (!map || !point) return null;
  const layers = cadPickLayerIds(map, includeContext);
  if (!layers.length) return null;
  try {
    return map.queryRenderedFeatures(point, { layers })[0] || null;
  } catch {
    return null;
  }
}

function createCadHoverPopup(share = false) {
  return new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    focusAfterOpen: false,
    offset: 14,
    className: share
      ? 'terrio-parcel-tooltip terrio-cad-share-tooltip'
      : 'terrio-parcel-tooltip',
    maxWidth: share ? '240px' : '259px',
  });
}

function isCadCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

function removeCadLayers(map) {
  if (!map) return;
  [
    CAD_SELECTED_LAYER_ID,
    CAD_SELECTED_GLOW_LAYER_ID,
    CAD_HOVER_LAYER_ID,
    CAD_LINE_LAYER_ID,
    CAD_FILL_LAYER_ID,
  ].forEach((layerId) => {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  });
  removeCadMasterplanLayers(map, {
    extraSymbolIds: [CAD_LABEL_LAYER_ID],
    extraSources: [CAD_SOURCE_ID],
  });
}

function cadHoverLinePaint(paintMode, hoverLineColor) {
  if (paintMode === 'align') {
    return {
      'line-color': CAD_PARCEL_STOCK_STYLE.alignLineColor,
      'line-width': CAD_PARCEL_STOCK_STYLE.alignLineWidth,
      'line-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        CAD_PARCEL_STOCK_STYLE.lineOpacityHover,
        0,
      ],
    };
  }
  return {
    'line-color': hoverLineColor || CAD_PARCEL_STOCK_STYLE.lineColorHover,
    'line-width': paintMode === 'share'
      ? CAD_PARCEL_STOCK_STYLE.shareLineWidthHover
      : CAD_PARCEL_STOCK_STYLE.lineWidthHover,
    'line-opacity': [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      CAD_PARCEL_STOCK_STYLE.lineOpacityHover,
      0,
    ],
  };
}

function cadSelectedLineLayout() {
  return { 'line-join': 'round', 'line-cap': 'round' };
}

function applyCadSelectedLayerPaint(map) {
  if (!map) return;
  const layout = cadSelectedLineLayout();
  if (map.getLayer(CAD_SELECTED_GLOW_LAYER_ID)) {
    map.setPaintProperty(
      CAD_SELECTED_GLOW_LAYER_ID,
      'line-color',
      CAD_PARCEL_STOCK_STYLE.lineColorSelectedGlow,
    );
    map.setPaintProperty(
      CAD_SELECTED_GLOW_LAYER_ID,
      'line-width',
      CAD_PARCEL_STOCK_STYLE.lineWidthSelectedGlow,
    );
    map.setPaintProperty(
      CAD_SELECTED_GLOW_LAYER_ID,
      'line-blur',
      CAD_PARCEL_STOCK_STYLE.lineBlurSelectedGlow,
    );
    map.setPaintProperty(
      CAD_SELECTED_GLOW_LAYER_ID,
      'line-opacity',
      CAD_PARCEL_STOCK_STYLE.lineOpacitySelectedGlow,
    );
    map.setLayoutProperty(CAD_SELECTED_GLOW_LAYER_ID, 'line-join', layout['line-join']);
    map.setLayoutProperty(CAD_SELECTED_GLOW_LAYER_ID, 'line-cap', layout['line-cap']);
  }
  if (map.getLayer(CAD_SELECTED_LAYER_ID)) {
    map.setPaintProperty(
      CAD_SELECTED_LAYER_ID,
      'line-color',
      CAD_PARCEL_STOCK_STYLE.lineColorSelected,
    );
    map.setPaintProperty(
      CAD_SELECTED_LAYER_ID,
      'line-width',
      CAD_PARCEL_STOCK_STYLE.lineWidthSelected,
    );
    map.setPaintProperty(CAD_SELECTED_LAYER_ID, 'line-opacity', 1);
    map.setLayoutProperty(CAD_SELECTED_LAYER_ID, 'line-join', layout['line-join']);
    map.setLayoutProperty(CAD_SELECTED_LAYER_ID, 'line-cap', layout['line-cap']);
  }
}

function ensureCadHoverLayer(map, paintMode, hoverLineColor) {
  const paint = cadHoverLinePaint(paintMode, hoverLineColor);
  if (!map.getLayer(CAD_HOVER_LAYER_ID)) {
    const layer = {
      id: CAD_HOVER_LAYER_ID,
      type: 'line',
      source: CAD_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint,
    };
    const beforeId = map.getLayer(CAD_SELECTED_GLOW_LAYER_ID)
      ? CAD_SELECTED_GLOW_LAYER_ID
      : (map.getLayer(CAD_SELECTED_LAYER_ID) ? CAD_SELECTED_LAYER_ID : undefined);
    if (beforeId) map.addLayer(layer, beforeId);
    else map.addLayer(layer);
    return;
  }
  map.setPaintProperty(CAD_HOVER_LAYER_ID, 'line-color', paint['line-color']);
  map.setPaintProperty(CAD_HOVER_LAYER_ID, 'line-width', paint['line-width']);
  map.setPaintProperty(CAD_HOVER_LAYER_ID, 'line-opacity', paint['line-opacity']);
}

function resolveCadPaintMode({ alignMode = false, shareOutline = false } = {}) {
  if (alignMode) return 'align';
  if (shareOutline) return 'share';
  return 'stock';
}

function applyCadLotKindFilters(map, paintMode) {
  const lotFilter = paintMode === 'align' ? null : CAD_LOT_KIND_FILTER;
  [CAD_FILL_LAYER_ID, CAD_LINE_LAYER_ID, CAD_HOVER_LAYER_ID].forEach((layerId) => {
    if (!map.getLayer(layerId)) return;
    try { map.setFilter(layerId, lotFilter); } catch (_) { /* estilo a medias */ }
  });
}

function applyCadStockPaint(map, visibleIds, paintMode = 'stock', hoverLineColor = null) {
  if (!map?.getLayer(CAD_FILL_LAYER_ID)) return;
  map.setPaintProperty(CAD_FILL_LAYER_ID, 'fill-color', buildCadFillColorExpression(visibleIds));
  map.setPaintProperty(CAD_FILL_LAYER_ID, 'fill-antialias', false);
  const fillOpacity = paintMode === 'align'
    ? CAD_PARCEL_STOCK_STYLE.alignFillOpacity
    : (paintMode === 'share'
      ? buildCadShareFillOpacityExpression(visibleIds)
      : buildCadFillOpacityExpression(visibleIds));
  map.setPaintProperty(CAD_FILL_LAYER_ID, 'fill-opacity', fillOpacity);
  if (!map.getLayer(CAD_LINE_LAYER_ID)) return;
  const lineColor = paintMode === 'align'
    ? CAD_PARCEL_STOCK_STYLE.alignLineColor
    : (paintMode === 'share'
      ? CAD_PARCEL_STOCK_STYLE.shareLineColor
      : buildCadLineColorExpression(visibleIds));
  const lineOpacity = paintMode === 'align'
    ? CAD_PARCEL_STOCK_STYLE.alignLineOpacity
    : (paintMode === 'share'
      ? buildCadShareLineOpacityExpression(visibleIds)
      : buildCadLineOpacityExpression(visibleIds));
  const lineWidth = paintMode === 'align'
    ? CAD_PARCEL_STOCK_STYLE.alignLineWidth
    : (paintMode === 'share'
      ? CAD_PARCEL_STOCK_STYLE.shareLineWidth
      : CAD_PARCEL_STOCK_STYLE.lineWidth);
  map.setPaintProperty(CAD_LINE_LAYER_ID, 'line-color', lineColor);
  map.setPaintProperty(CAD_LINE_LAYER_ID, 'line-opacity', lineOpacity);
  map.setPaintProperty(CAD_LINE_LAYER_ID, 'line-width', lineWidth);
  ensureCadHoverLayer(map, paintMode, hoverLineColor);
  applyCadLotKindFilters(map, paintMode);
  const chrome = paintMode === 'align' ? 'none' : 'visible';
  [CAD_HOVER_LAYER_ID, CAD_SELECTED_GLOW_LAYER_ID, CAD_SELECTED_LAYER_ID, CAD_LABEL_LAYER_ID].forEach((layerId) => {
    if (!map.getLayer(layerId)) return;
    const apply = () => {
      try { map.setLayoutProperty(layerId, 'visibility', chrome); } catch (_) { /* estilo a medias */ }
    };
    if (layerId === CAD_LABEL_LAYER_ID) {
      try {
        if (map.getLayoutProperty(layerId, 'visibility') === chrome) return;
      } catch (_) { /* unset */ }
      scheduleCadSymbolOp(CAD_LABEL_LAYER_ID, apply);
      return;
    }
    apply();
  });
}

function cadParcelLabelMinZoom(overlayMinZoom) {
  const base = CAD_PARCEL_STOCK_STYLE.labelMinZoom;
  if (!Number.isFinite(overlayMinZoom)) return base;
  return Math.max(overlayMinZoom, base);
}

function cadChipLabelLayout(visible) {
  return {
    'icon-image': buildCadLotChipImageExpression(),
    'icon-size': cadLotChipIconSizeExpression(),
    'icon-text-fit': 'none',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-padding': 0,
    'icon-anchor': 'center',
    'icon-pitch-alignment': 'viewport',
    'icon-rotation-alignment': 'viewport',
    'text-field': '',
    'symbol-placement': 'point',
    'symbol-avoid-edges': false,
    visibility: visible,
  };
}

function cadChipLabelPaint(visibleIds) {
  return {
    'icon-opacity': cadLotChipIconOpacityExpression(visibleIds),
    'icon-opacity-transition': { duration: 80 },
  };
}

function applyCadChipLabelLayout(map, visible) {
  scheduleCadSymbolOp(CAD_LABEL_LAYER_ID, () => {
    if (!map.getLayer(CAD_LABEL_LAYER_ID)) return;
    Object.entries(cadChipLabelLayout(visible)).forEach(([key, value]) => {
      try { map.setLayoutProperty(CAD_LABEL_LAYER_ID, key, value); } catch (_) { /* estilo a medias */ }
    });
  });
}

function applyCadChipLabelPaint(map, visibleIds) {
  Object.entries(cadChipLabelPaint(visibleIds)).forEach(([key, value]) => {
    try { map.setPaintProperty(CAD_LABEL_LAYER_ID, key, value); } catch (_) { /* estilo a medias */ }
  });
}

function ensureCadLabelLayer(map, paintMode, overlayMinZoom, features, visibleIds) {
  const minzoom = cadParcelLabelMinZoom(overlayMinZoom);
  const visible = paintMode === 'align' ? 'none' : 'visible';
  ensureCadLotChipImages(map, features);
  if (!map.getLayer(CAD_LABEL_LAYER_ID)) {
    scheduleCadSymbolOp(CAD_LABEL_LAYER_ID, () => {
      if (!map.getSource(CAD_SOURCE_ID)) return;
      if (!map.getLayer(CAD_LABEL_LAYER_ID)) {
        map.addLayer({
          id: CAD_LABEL_LAYER_ID,
          type: 'symbol',
          source: CAD_SOURCE_ID,
          minzoom,
          filter: ['!=', ['to-string', ['coalesce', ['get', 'map_label'], '']], ''],
          layout: cadChipLabelLayout(visible),
          paint: cadChipLabelPaint(visibleIds),
        });
        return;
      }
      try {
        map.setLayoutProperty(CAD_LABEL_LAYER_ID, 'visibility', visible);
      } catch (_) { /* estilo a medias */ }
    });
    return;
  }
  try {
    if (map.getLayoutProperty(CAD_LABEL_LAYER_ID, 'visibility') !== visible) {
      applyCadChipLabelLayout(map, visible);
    }
  } catch (_) {
    applyCadChipLabelLayout(map, visible);
  }
  applyCadChipLabelPaint(map, visibleIds);
  try { map.setLayerZoomRange(CAD_LABEL_LAYER_ID, minzoom, 24); } catch (_) { /* estilo a medias */ }
}

function applyCadLayerMinZoom(map, minZoom) {
  if (!Number.isFinite(minZoom)) return;
  [
    CAD_FILL_LAYER_ID,
    CAD_LINE_LAYER_ID,
    CAD_HOVER_LAYER_ID,
    CAD_SELECTED_GLOW_LAYER_ID,
    CAD_SELECTED_LAYER_ID,
    ...CAD_MASTERPLAN_LAYER_IDS.filter((id) => !CAD_TREE_LAYER_IDS.includes(id)),
  ].forEach((layerId) => {
    if (map.getLayer(layerId)) {
      try { map.setLayerZoomRange(layerId, minZoom, 24); } catch (_) { /* estilo a medias */ }
    }
  });
}

function ensureCadLayers(
  map,
  geojson,
  visibleIds,
  minZoom,
  paintMode,
  hoverLineColor,
  masterplanEnabled = true,
) {
  bumpCadSymbolOpEpoch();
  if (!map.getSource(CAD_SOURCE_ID)) {
    map.addSource(CAD_SOURCE_ID, {
      type: 'geojson',
      data: geojson,
      promoteId: 'id',
      ...CAD_GEOJSON_SOURCE_OPTIONS,
    });
  } else {
    const source = map.getSource(CAD_SOURCE_ID);
    if (source && typeof source.setData === 'function') source.setData(geojson);
  }

  ensureCadMasterplanLayers(map, geojson, {
    paintMode,
    minZoom,
    beforeId: map.getLayer(CAD_FILL_LAYER_ID) ? CAD_FILL_LAYER_ID : undefined,
    lotFillLayerId: CAD_FILL_LAYER_ID,
    parcelsSourceId: CAD_SOURCE_ID,
    contextEnabled: masterplanEnabled,
  });

  if (!map.getLayer(CAD_FILL_LAYER_ID)) {
    map.addLayer({
      id: CAD_FILL_LAYER_ID,
      type: 'fill',
      source: CAD_SOURCE_ID,
      ...(paintMode === 'align' ? {} : { filter: CAD_LOT_KIND_FILTER }),
      paint: {
        'fill-antialias': false,
        'fill-color': buildCadFillColorExpression(visibleIds),
        'fill-opacity': buildCadFillOpacityExpression(visibleIds),
      },
    });
  }

  // Copas encima del fill/línea (si no, el lote opaco corta la hilera de calle).
  keepCadTreesAboveLots(map);

  if (!map.getLayer(CAD_LINE_LAYER_ID)) {
    map.addLayer({
      id: CAD_LINE_LAYER_ID,
      type: 'line',
      source: CAD_SOURCE_ID,
      ...(paintMode === 'align' ? {} : { filter: CAD_LOT_KIND_FILTER }),
      paint: {
        'line-color': buildCadLineColorExpression(visibleIds),
        'line-width': CAD_PARCEL_STOCK_STYLE.lineWidth,
        'line-opacity': buildCadLineOpacityExpression(visibleIds),
      },
    });
  }

  applyCadStockPaint(map, visibleIds, paintMode, hoverLineColor);

  const selectedLayout = cadSelectedLineLayout();
  if (!map.getLayer(CAD_SELECTED_GLOW_LAYER_ID)) {
    const glowLayer = {
      id: CAD_SELECTED_GLOW_LAYER_ID,
      type: 'line',
      source: CAD_SOURCE_ID,
      layout: selectedLayout,
      filter: EMPTY_SELECTED_LOT_FILTER,
      paint: {
        'line-color': CAD_PARCEL_STOCK_STYLE.lineColorSelectedGlow,
        'line-width': CAD_PARCEL_STOCK_STYLE.lineWidthSelectedGlow,
        'line-blur': CAD_PARCEL_STOCK_STYLE.lineBlurSelectedGlow,
        'line-opacity': CAD_PARCEL_STOCK_STYLE.lineOpacitySelectedGlow,
      },
    };
    // Debajo del contorno nítido si ese ya estaba (HMR / re-paint).
    if (map.getLayer(CAD_SELECTED_LAYER_ID)) {
      map.addLayer(glowLayer, CAD_SELECTED_LAYER_ID);
    } else {
      map.addLayer(glowLayer);
    }
  }

  if (!map.getLayer(CAD_SELECTED_LAYER_ID)) {
    map.addLayer({
      id: CAD_SELECTED_LAYER_ID,
      type: 'line',
      source: CAD_SOURCE_ID,
      layout: selectedLayout,
      filter: EMPTY_SELECTED_LOT_FILTER,
      paint: {
        'line-color': CAD_PARCEL_STOCK_STYLE.lineColorSelected,
        'line-width': CAD_PARCEL_STOCK_STYLE.lineWidthSelected,
        'line-opacity': 1,
      },
    });
  }
  applyCadSelectedLayerPaint(map);

  ensureCadLabelLayer(map, paintMode, minZoom, geojson?.features, visibleIds);
  applyCadLayerMinZoom(map, minZoom);
  // Imagen / masterplan / labels se agregan al tope; los pines vuelven arriba.
  moveCadProjectPinsForeground(map);
}

function setCadFeatureState(map, parcelId, state) {
  if (!map || parcelId == null) return;
  try {
    map.setFeatureState({ source: CAD_SOURCE_ID, id: parcelId }, state);
  } catch (_) {
    // source todavía no promovió el id
  }
}

const DEFAULT_FIT_MAX_ZOOM = 18.5;
const DEFAULT_FIT_DURATION_MS = 700;

function resolveFitPadding(padding, extra = 0) {
  if (!extra) return padding;
  if (typeof padding === 'number') return padding + extra;
  const base = padding || {};
  return {
    top: (Number(base.top) || 0) + extra,
    bottom: (Number(base.bottom) || 0) + extra,
    left: (Number(base.left) || 0) + extra,
    right: (Number(base.right) || 0) + extra,
  };
}

function isValidLngLatBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length < 4) return false;
  const west = Number(bbox[0]);
  const south = Number(bbox[1]);
  const east = Number(bbox[2]);
  const north = Number(bbox[3]);
  if (![west, south, east, north].every(Number.isFinite)) return false;
  return south >= -90 && south <= 90 && north >= -90 && north <= 90
    && west >= -180 && west <= 180 && east >= -180 && east <= 180;
}

function isLngLatInBbox(lngLat, bbox) {
  if (!lngLat || !isValidLngLatBbox(bbox)) return false;
  const lng = Number(lngLat.lng);
  const lat = Number(lngLat.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  return lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3];
}

function fitBbox(map, bbox, padding, maxZoom = DEFAULT_FIT_MAX_ZOOM, { pitch, duration } = {}) {
  if (!map || !isValidLngLatBbox(bbox)) return;
  const bounds = new maplibregl.LngLatBounds(
    [bbox[0], bbox[1]],
    [bbox[2], bbox[3]],
  );
  const options = {
    padding,
    maxZoom,
    duration: Number.isFinite(duration) ? duration : DEFAULT_FIT_DURATION_MS,
  };
  if (Number.isFinite(pitch)) options.pitch = pitch;
  map.fitBounds(bounds, options);
}

function syncSelection(map, geojson, selectedId) {
  if (!map.getSource(CAD_SOURCE_ID)) return;
  const current = selectedId || '';
  const filter = buildSelectedLotIdFilter(current);
  [CAD_SELECTED_GLOW_LAYER_ID, CAD_SELECTED_LAYER_ID].forEach((layerId) => {
    if (map.getLayer(layerId)) map.setFilter(layerId, filter);
  });
  geojson.features.forEach((feature) => {
    const id = getCadParcelId(feature);
    if (!id) return;
    setCadFeatureState(map, id, { selected: id === current });
  });
}

/**
 * Overlay GeoJSON de custom_parcels sobre el mapa de Terrio.
 * Limpia source, layers y handlers al desmontar o al recargar el estilo.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean} options.enabled
 * @param {object|null} options.featureCollection
 * @param {string|null} options.selectedId
 * @param {string|null} [options.highlightedId]  ubicar sin abrir ficha (glow de selección)
 * @param {(id: string|null) => void} options.onSelect
 * @param {object} [options.fitPadding]
 * @param {boolean} [options.fitOnLoad]
 * @param {number} [options.fitMaxZoom]
 * @param {number} [options.locateMaxZoom]  tope al encuadrar una parcela (más lejos que fitMaxZoom)
 * @param {number} [options.locatePaddingExtra]  margen extra en px alrededor de la parcela
 * @param {boolean} [options.pickEnabled]
 * @param {boolean} [options.pickContext]  Listas manage: click en calle/verde/predio abre ficha para corregir tipo
 * @param {string|number} [options.fitNonce]  cambia para reencuadrar (p. ej. al apoyar el sticker)
 * @param {Record<string, boolean>|null} [options.stockVisibility]  chips de estado; null = todos
 * @param {number} [options.minZoom]  no pintar polígonos bajo este zoom (mapa país)
 * @param {boolean} [options.alignMode]  guía de encaje: contorno nítido, sin colores de stock
 * @param {boolean} [options.masterplanEnabled]  calles/plaza/árboles; false si se pinta el PNG
 * @param {boolean} [options.shareOutline]  vista compartida: líneas blancas + relleno de stock suave
 * @param {string|null} [options.hoverLineColor]  hover de marca; si no, teal Terrio
 * @param {number} [options.initialPitch]  solo el primer encuadre (share CAD mapa)
 * @param {number} [options.initialFitDuration]  primer encuadre; 0 = jump (sin volar desde el país)
 * @param {() => void} [options.onInitialFit]  overlay / share: el mapa ya encuadró
 */
export function useCadParcelsLayer({
  mapInstanceRef,
  enabled,
  featureCollection,
  selectedId,
  highlightedId = null,
  onSelect,
  fitPadding,
  fitOnLoad = true,
  fitMaxZoom = DEFAULT_FIT_MAX_ZOOM,
  locateMaxZoom,
  locatePaddingExtra = 0,
  pickEnabled = true,
  pickContext = false,
  fitNonce = '',
  stockVisibility = null,
  minZoom,
  alignMode = false,
  masterplanEnabled = true,
  shareOutline = false,
  hoverLineColor = null,
  initialPitch,
  initialFitDuration,
  onInitialFit,
  /** Hover del mapa sobre un lote vendible: id o null. No dispara el hover de la card. */
  onHoverParcelId = null,
}) {
  const geojson = useMemo(
    () => decorateCadParcelsForMap(normalizeCadFeatureCollection(featureCollection)),
    [featureCollection],
  );
  const visibleStockIds = useMemo(
    () => visibleCadStockStatusIds(stockVisibility),
    [stockVisibility],
  );
  const geojsonRef = useRef(geojson);
  const enabledRef = useRef(enabled);
  const selectedIdRef = useRef(selectedId);
  const highlightedIdRef = useRef(highlightedId);
  const onSelectRef = useRef(onSelect);
  const fitPaddingRef = useRef(fitPadding);
  const fitMaxZoomRef = useRef(fitMaxZoom);
  const locateMaxZoomRef = useRef(locateMaxZoom);
  const locatePaddingExtraRef = useRef(locatePaddingExtra);
  const fittedKeyRef = useRef('');
  const paintMode = resolveCadPaintMode({ alignMode, shareOutline });
  const paintModeRef = useRef(paintMode);
  paintModeRef.current = paintMode;
  const masterplanEnabledRef = useRef(masterplanEnabled);
  masterplanEnabledRef.current = masterplanEnabled;
  const hoverLineColorRef = useRef(hoverLineColor);
  hoverLineColorRef.current = hoverLineColor;
  const hoverIdRef = useRef(null);
  const panelHoverIdRef = useRef(null);
  const popupRef = useRef(null);
  /** Tooltip del hover nacido en la card. Lo llena el efecto del mapa. */
  const panelPopupApiRef = useRef(null);
  const onHoverParcelIdRef = useRef(null);
  const notifiedMapHoverRef = useRef(null);
  const pickEnabledRef = useRef(pickEnabled);
  const pickContextRef = useRef(pickContext);
  const fitNonceRef = useRef(fitNonce);
  const visibleStockIdsRef = useRef(visibleStockIds);
  const minZoomRef = useRef(minZoom);
  const initialPitchRef = useRef(initialPitch);
  const initialFitDurationRef = useRef(initialFitDuration);
  const onInitialFitRef = useRef(onInitialFit);
  const didApplyInitialPitchRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const didNotifyInitialFitRef = useRef(false);
  const skipFitNonceEffectRef = useRef(true);

  geojsonRef.current = geojson;
  enabledRef.current = enabled;
  selectedIdRef.current = selectedId;
  highlightedIdRef.current = highlightedId;
  onSelectRef.current = onSelect;
  fitPaddingRef.current = fitPadding;
  fitMaxZoomRef.current = fitMaxZoom;
  locateMaxZoomRef.current = locateMaxZoom;
  locatePaddingExtraRef.current = locatePaddingExtra;
  pickEnabledRef.current = pickEnabled;
  pickContextRef.current = pickContext;
  fitNonceRef.current = fitNonce;
  visibleStockIdsRef.current = visibleStockIds;
  minZoomRef.current = minZoom;
  initialPitchRef.current = initialPitch;
  initialFitDurationRef.current = initialFitDuration;
  onInitialFitRef.current = onInitialFit;
  onHoverParcelIdRef.current = onHoverParcelId;

  const notifyMapHover = (parcelId) => {
    const next = parcelId != null && parcelId !== '' ? String(parcelId) : null;
    if (notifiedMapHoverRef.current === next) return;
    notifiedMapHoverRef.current = next;
    onHoverParcelIdRef.current?.(next);
  };

  useEffect(() => {
    if (!enabled) return undefined;
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    let cancelled = false;
    const unbinders = [];
    let pinnedId = null;
    let popupParcelId = null;

    const clearMapHover = () => {
      if (hoverIdRef.current == null) return;
      const mapHoverId = hoverIdRef.current;
      hoverIdRef.current = null;
      if (mapHoverId !== panelHoverIdRef.current) {
        setCadFeatureState(map, mapHoverId, { hover: false });
      }
    };

    const hidePopupNow = () => {
      popupParcelId = null;
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };

    const dismissHoverChrome = () => {
      hidePopupNow();
      pinnedId = null;
      clearMapHover();
      notifyMapHover(null);
    };

    const isNodeInPopup = (node) => {
      const el = popupRef.current?.getElement();
      return Boolean(el && node instanceof Node && el.contains(node));
    };

    const bindShareTooltipBridge = (popup) => {
      const el = popup.getElement();
      if (!el || el.dataset.cadBridge === '1') return;
      el.dataset.cadBridge = '1';
      el.addEventListener('mouseleave', (event) => {
        if (isCadCoarsePointer()) return;
        const canvas = map.getCanvas();
        if (event.relatedTarget instanceof Node && canvas.contains(event.relatedTarget)) return;
        dismissHoverChrome();
      });
      el.addEventListener('click', (event) => {
        const card = event.target.closest('[data-cad-parcel-id]');
        const id = card?.getAttribute('data-cad-parcel-id');
        if (!id) return;
        event.preventDefault();
        event.stopPropagation();
        dismissHoverChrome();
        onSelectRef.current?.(id);
      });
      el.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const card = event.target.closest('[data-cad-parcel-id]');
        const id = card?.getAttribute('data-cad-parcel-id');
        if (!id) return;
        event.preventDefault();
        dismissHoverChrome();
        onSelectRef.current?.(id);
      });
    };

    const showPopup = (lngLat, html, parcelId) => {
      if (!lngLat || !html) {
        hidePopupNow();
        return;
      }
      const share = paintModeRef.current === 'share';
      if (popupRef.current && popupRef.current._cadShare !== share) {
        popupRef.current.remove();
        popupRef.current = null;
        popupParcelId = null;
      }
      if (!popupRef.current) {
        popupRef.current = createCadHoverPopup(share);
        popupRef.current._cadShare = share;
      }
      if (popupParcelId === parcelId) {
        popupRef.current.setLngLat(lngLat);
        return;
      }
      popupParcelId = parcelId;
      popupRef.current.setLngLat(lngLat).setHTML(html).addTo(map);
      const el = popupRef.current.getElement();
      if (el) {
        if (parcelId) el.setAttribute('data-cad-parcel-id', parcelId);
        else el.removeAttribute('data-cad-parcel-id');
      }
      bindShareTooltipBridge(popupRef.current);
    };

    panelPopupApiRef.current = {
      show(parcelId) {
        const feature = (geojsonRef.current?.features || []).find(
          (item) => getCadParcelId(item) === String(parcelId),
        );
        if (!feature || !isCadSellableLot(feature)) {
          hidePopupNow();
          return;
        }
        const bbox = computeGeometryBbox(feature.geometry);
        if (!bbox) {
          hidePopupNow();
          return;
        }
        showPopup(
          { lng: (bbox[0] + bbox[2]) / 2, lat: (bbox[1] + bbox[3]) / 2 },
          tooltipHtmlFor(feature),
          String(parcelId),
        );
      },
      hide() {
        hidePopupNow();
      },
    };

    const tooltipHtmlFor = (feature) => (
      paintModeRef.current === 'share'
        ? buildCadShareParcelTooltipHtml(feature)
        : buildCadParcelTooltipHtml(feature)
    );

    const bindHandlers = () => {
      unbinders.forEach((unbind) => unbind());
      unbinders.length = 0;

      const setPickCursor = (clickable) => {
        try {
          map.getCanvas().style.cursor = clickable ? 'pointer' : '';
        } catch (_) { /* mapa ya destruido */ }
      };

      const onMapMouseMove = (event) => {
        if (!pickEnabledRef.current) return;
        if (event.originalEvent?.buttons) return;
        if (isCadCoarsePointer() && pinnedId) return;
        if (
          queryReferencePlaceAtPoint(map, event.point)
          || queryCadProjectPinAtPoint(map, event.point)
        ) {
          dismissHoverChrome();
          return;
        }
        const feature = queryCadParcelAtPoint(map, event.point, pickContextRef.current);
        const nextId = getCadParcelId(feature);
        if (nextId != null) {
          if (hoverIdRef.current !== nextId) {
            if (hoverIdRef.current != null && hoverIdRef.current !== panelHoverIdRef.current) {
              setCadFeatureState(map, hoverIdRef.current, { hover: false });
            }
            hoverIdRef.current = nextId;
            setCadFeatureState(map, nextId, { hover: true });
          }
          setPickCursor(true);
          if (!isCadSellableLot(feature)) {
            hidePopupNow();
            notifyMapHover(null);
            return;
          }
          notifyMapHover(nextId);
          if (popupParcelId === nextId && popupRef.current) {
            popupRef.current.setLngLat(event.lngLat);
          } else {
            showPopup(event.lngLat, tooltipHtmlFor(feature), nextId);
          }
          return;
        }
        const insideLoteo = isLngLatInBbox(
          event.lngLat,
          computeFeatureCollectionBbox(geojsonRef.current),
        );
        if (insideLoteo && (hoverIdRef.current || popupRef.current)) return;
        setPickCursor(false);
        dismissHoverChrome();
      };
      const onParcelClick = (event) => {
        if (!pickEnabledRef.current) return;
        const feature = event.features?.[0] || queryCadParcelAtPoint(map, event.point, pickContextRef.current);
        const id = getCadParcelId(feature);
        if (!id) return;
        if (event.originalEvent) event.originalEvent.__cadParcelClick = true;
        if (!isCadSellableLot(feature)) {
          dismissHoverChrome();
          onSelectRef.current?.(id);
          return;
        }
        if (isCadCoarsePointer()) {
          if (pinnedId === id) {
            dismissHoverChrome();
            onSelectRef.current?.(id);
            return;
          }
          if (hoverIdRef.current != null && hoverIdRef.current !== id
            && hoverIdRef.current !== panelHoverIdRef.current) {
            setCadFeatureState(map, hoverIdRef.current, { hover: false });
          }
          pinnedId = id;
          hoverIdRef.current = id;
          setCadFeatureState(map, id, { hover: true });
          showPopup(event.lngLat, tooltipHtmlFor(feature), id);
          return;
        }
        dismissHoverChrome();
        onSelectRef.current?.(id);
      };
      const onMapClick = (event) => {
        if (!pickEnabledRef.current) return;
        if (event.originalEvent?.__cadParcelClick) return;
        if (queryCadParcelAtPoint(map, event.point, pickContextRef.current)) return;
        dismissHoverChrome();
        onSelectRef.current?.(null);
      };
      const onCanvasMouseLeave = (event) => {
        if (isCadCoarsePointer()) return;
        setPickCursor(false);
        if (isNodeInPopup(event.relatedTarget)) return;
        dismissHoverChrome();
      };

      map.on('mousemove', onMapMouseMove);
      cadPickLayerIds(map, pickContextRef.current).forEach((layerId) => {
        map.on('click', layerId, onParcelClick);
      });
      map.on('click', onMapClick);
      const canvas = map.getCanvas();
      canvas.addEventListener('mouseleave', onCanvasMouseLeave);

      unbinders.push(() => {
        map.off('mousemove', onMapMouseMove);
        [...CAD_PICK_LAYER_IDS, ...CAD_OVERLAY_HIT_LAYER_IDS].forEach((layerId) => {
          map.off('click', layerId, onParcelClick);
        });
        map.off('click', onMapClick);
        canvas.removeEventListener('mouseleave', onCanvasMouseLeave);
      });
    };

    const paint = () => {
      if (cancelled) return;
      try {
        const data = geojsonRef.current?.features?.length ? geojsonRef.current : EMPTY_COLLECTION;
        ensureCadLayers(
          map,
          data,
          visibleStockIdsRef.current,
          minZoomRef.current,
          paintModeRef.current,
          hoverLineColorRef.current,
          masterplanEnabledRef.current,
        );
        bindHandlers();
        syncSelection(map, data, selectedIdRef.current || highlightedIdRef.current);

        const notifyInitialFit = () => {
          if (didNotifyInitialFitRef.current) return;
          didNotifyInitialFitRef.current = true;
          onInitialFitRef.current?.();
        };

        if (fitOnLoad && data.features.length > 0) {
          const key = `${fitNonceRef.current}:${data.features.length}:${getCadParcelId(data.features[0])}`;
          if (fittedKeyRef.current !== key) {
            fittedKeyRef.current = key;
            const pitch = !didApplyInitialPitchRef.current
              && Number.isFinite(initialPitchRef.current)
              ? initialPitchRef.current
              : undefined;
            if (Number.isFinite(pitch)) didApplyInitialPitchRef.current = true;
            const isFirstFit = !didInitialFitRef.current;
            didInitialFitRef.current = true;
            const duration = isFirstFit && Number.isFinite(initialFitDurationRef.current)
              ? initialFitDurationRef.current
              : DEFAULT_FIT_DURATION_MS;
            fitBbox(
              map,
              computeFeatureCollectionBbox(data),
              fitPaddingRef.current,
              fitMaxZoomRef.current,
              { pitch, duration },
            );
            notifyInitialFit();
          }
        } else {
          notifyInitialFit();
        }
      } catch (error) {
        console.error('CAD showroom: no se pudo pintar el overlay', error);
      }
    };

    const stopReady = whenMapStyleReady(map, paint);

    const onStyleData = () => {
      if (!map.isStyleLoaded()) return;
      if (map.getLayer(CAD_FILL_LAYER_ID) && map.getSource(CAD_SOURCE_ID)) return;
      paint();
    };
    map.on('styledata', onStyleData);

    return () => {
      cancelled = true;
      panelPopupApiRef.current = null;
      notifyMapHover(null);
      stopReady();
      map.off('styledata', onStyleData);
      unbinders.forEach((unbind) => unbind());
      hoverIdRef.current = null;
      panelHoverIdRef.current = null;
      fittedKeyRef.current = '';
      didApplyInitialPitchRef.current = false;
      didInitialFitRef.current = false;
      didNotifyInitialFitRef.current = false;
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      try {
        map.getCanvas().style.cursor = '';
        removeCadLayers(map);
      } catch (_) {
        // mapa ya destruido
      }
    };
  }, [enabled, fitOnLoad, mapInstanceRef]);

  useEffect(() => {
    if (skipFitNonceEffectRef.current) {
      skipFitNonceEffectRef.current = false;
      return;
    }
    if (!enabledRef.current || !fitOnLoad) return;
    const map = mapInstanceRef.current;
    if (!map?.getSource(CAD_SOURCE_ID)) return;
    const data = geojsonRef.current;
    if (!data?.features?.length) return;
    // El setup effect ya no se remonta por fitNonce: reencuadra sin teardown.
    fittedKeyRef.current = `${fitNonce}:${data.features.length}:${getCadParcelId(data.features[0])}`;
    fitBbox(
      map,
      computeFeatureCollectionBbox(data),
      fitPaddingRef.current,
      fitMaxZoomRef.current,
    );
  }, [fitNonce, fitOnLoad, mapInstanceRef]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map?.getSource(CAD_SOURCE_ID)) return;
    ensureCadLayers(
      map,
      geojson.features.length ? geojson : EMPTY_COLLECTION,
      visibleStockIds,
      minZoom,
      paintMode,
      hoverLineColor,
      masterplanEnabled,
    );
    syncSelection(map, geojson, selectedIdRef.current || highlightedIdRef.current);
  }, [
    enabled,
    geojson,
    hoverLineColor,
    mapInstanceRef,
    masterplanEnabled,
    minZoom,
    paintMode,
    visibleStockIds,
  ]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map?.getSource(CAD_SOURCE_ID)) return;
    // Solo el filtro de selección: no rearmar capas ni chips al abrir/cerrar un lote.
    syncSelection(map, geojsonRef.current, selectedId || highlightedId);
  }, [enabled, highlightedId, mapInstanceRef, selectedId]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map?.getLayer(CAD_FILL_LAYER_ID)) return;
    applyCadStockPaint(map, visibleStockIds, paintMode, hoverLineColor);
    if (map.getLayer(CAD_LABEL_LAYER_ID)) {
      applyCadChipLabelPaint(map, visibleStockIds);
    }
  }, [enabled, hoverLineColor, mapInstanceRef, paintMode, visibleStockIds]);

  const hoverParcel = useCallback((parcelId) => {
    if (!enabledRef.current) return;
    const map = mapInstanceRef.current;
    if (!map?.getSource(CAD_SOURCE_ID)) return;
    const nextId = parcelId ? String(parcelId) : null;
    if (panelHoverIdRef.current === nextId) return;
    const prevId = panelHoverIdRef.current;
    panelHoverIdRef.current = nextId;
    if (prevId != null && prevId !== hoverIdRef.current) {
      setCadFeatureState(map, prevId, { hover: false });
    }
    if (nextId != null) {
      setCadFeatureState(map, nextId, { hover: true });
      panelPopupApiRef.current?.show(nextId);
    } else {
      panelPopupApiRef.current?.hide();
    }
  }, [mapInstanceRef]);

  const locateParcel = useCallback((parcelId) => {
    if (!enabledRef.current) return;
    const map = mapInstanceRef.current;
    if (!map || !parcelId) return;
    const feature = geojsonRef.current.features.find(
      (item) => getCadParcelId(item) === String(parcelId),
    );
    const locateZoom = Number.isFinite(locateMaxZoomRef.current)
      ? locateMaxZoomRef.current
      : fitMaxZoomRef.current;
    fitBbox(
      map,
      computeGeometryBbox(feature?.geometry),
      resolveFitPadding(fitPaddingRef.current, locatePaddingExtraRef.current),
      locateZoom,
    );
  }, [mapInstanceRef]);

  const fitAllParcels = useCallback((collection) => {
    if (!enabledRef.current) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    const data = collection
      ? normalizeCadFeatureCollection(collection)
      : geojsonRef.current;
    fitBbox(
      map,
      computeFeatureCollectionBbox(data),
      fitPaddingRef.current,
      fitMaxZoomRef.current,
    );
  }, [mapInstanceRef]);

  return { locateParcel, hoverParcel, fitAllParcels };
}
